import {
  EditableRowsProvider,
  EditableSettingRow,
  SettingRowsCard,
} from "@/app/Settings/Settings/Components/EditableSettingRow";
import { updateIntegrationMutation } from "@/app/Settings/Settings/GraphQL/integrationMutations";
import { IntegrationRecord } from "@/app/Settings/Settings/GraphQL/integrationQueries";
import { WarningBanner } from "@/lib/Components/Banners/WarningBanner";
import { TextInput } from "@/lib/Components/Form/Inputs/TextInput";
import { useGqlMutation } from "@/lib/GraphQLCodegen/fetcher";

function extractSpreadsheetId(input: string): string {
  const trimmed = input.trim();
  const match = trimmed.match(/\/spreadsheets\/d\/([a-zA-Z0-9-_]+)/);
  return match ? match[1] : trimmed;
}

export function RoadsurferIntegrationScreen({
  integration,
}: {
  integration: IntegrationRecord;
}) {
  const { mutateAsync } = useGqlMutation(updateIntegrationMutation);
  const metadata = integration?.metadata;
  const importUrl = metadata?.import_key
    ? new URL(
        `/api/integrations/roadsurfer/import/${metadata.import_key}`,
        import.meta.env.VITE_API_URL,
      ).toString()
    : "";

  return (
    <EditableRowsProvider>
      <div className="space-y-6">
        {metadata?.last_error ? (
          <WarningBanner>
            <strong>Last import failed:</strong> {metadata.last_error}
            {metadata.last_error_at ? (
              <span className="ml-2 opacity-70">
                ({metadata.last_error_at})
              </span>
            ) : null}
          </WarningBanner>
        ) : null}
        <div className="rounded-md border p-4 text-sm">
          <h3 className="mb-2 font-medium">Setup instructions</h3>
          <ol className="list-decimal space-y-1 pl-5">
            <li>
              Open the deal sheet roadsurfer sends and share it with{" "}
              <code>imoova@imoova-194802.iam.gserviceaccount.com</code> as a
              Viewer. We only ever read it.
            </li>
            <li>
              Keep their own column order: VIN, licence plate, start station,
              end station, transfer start date, transfer end date, subgroup.
            </li>
            <li>
              Dates must be real dates, not text. Pasted <code>dd/mm/yyyy</code>{" "}
              text is read as <code>mm/dd</code> in a US locale sheet.
            </li>
            <li>Paste the sheet URL below.</li>
          </ol>
        </div>
        <SettingRowsCard
          title="Deal sheet"
          description="The sheet roadsurfer keeps their live relocations on. Rental length, mileage and extra nights come from the trip defaults and the supplier's mileage rule, not from the sheet."
        >
          <EditableSettingRow
            label="Deal sheet"
            value={
              metadata?.sheet_name ||
              (metadata?.spreadsheet_id ? "Configured" : "")
            }
            editClassName="max-w-2xl"
            initialValues={{
              spreadsheet_id: metadata?.spreadsheet_id ?? "",
              sheet_name: metadata?.sheet_name ?? "",
            }}
            onSave={(values) =>
              mutateAsync({
                id: integration.id,
                input: {
                  metadata: {
                    ...metadata,
                    spreadsheet_id: extractSpreadsheetId(values.spreadsheet_id),
                    sheet_name: values.sheet_name.trim(),
                  },
                },
              })
            }
          >
            <TextInput
              name="spreadsheet_id"
              label="Spreadsheet ID or URL"
              className="col-span-12"
              inputProps={{ placeholder: "Full sheet URL or the id from it" }}
            />
            <TextInput
              name="sheet_name"
              label="Sheet tab name"
              className="col-span-12"
              inputProps={{ placeholder: "Sheet1 (leave blank for first tab)" }}
            />
          </EditableSettingRow>
        </SettingRowsCard>
        {importUrl ? (
          <p className="text-sm">
            Pull the sheet now:{" "}
            <a className="text-primary underline" href={importUrl}>
              {importUrl}
            </a>
          </p>
        ) : null}
      </div>
    </EditableRowsProvider>
  );
}
